import { useReducer, useCallback, useMemo } from "react";
import { isEqual } from "@util/utils";

/**
 * 購物車商品項目介面
 * @typedef {Object} CartItem
 * @property {number} id - 商品ID
 * @property {string} sku - 商品SKU
 * @property {string} name - 商品名稱
 * @property {string} spec - 商品規格
 * @property {number} originalPrice - 原價
 * @property {number} currentPrice - 現價
 * @property {number} quantity - 數量
 */

/**
 * 加入商品時的插入位置
 * - PREPEND: 新商品排在最前面（預設）
 * - APPEND: 新商品排在最後面
 */
export const ADD_POSITION = {
	PREPEND: "prepend",
	APPEND: "append",
};

/**
 * 更新商品時的位置調整
 * - PREPEND: 將商品移到最前面
 * - APPEND: 將商品移到最後面
 * - KEEP: 保持原位（預設）
 */
export const UPDATE_POSITION = {
	PREPEND: "prepend",
	APPEND: "append",
	KEEP: "keep",
};

/**
 * 回饋類型常數
 */
export const FEEDBACK_TYPES = {
	ADD_SUCCESS: "ADD_SUCCESS",
	UPDATE_SUCCESS: "UPDATE_SUCCESS",
	REMOVE_SUCCESS: "REMOVE_SUCCESS",
	ERROR: "ERROR",
	CLEAR_SUCCESS: "CLEAR_SUCCESS",
};

/**
 * 回饋物件介面
 * @typedef {Object} FeedbackObject
 * @property {string} type - 回饋類型 (FEEDBACK_TYPES)
 * @property {string} message - 回饋訊息
 * @property {CartItem|null} item - 相關的商品物件
 * @property {number} timestamp - 時間戳記
 */

/**
 * Action Types 常數
 */
const CART_ACTIONS = {
	ADD_ITEM: "ADD_ITEM",
	UPDATE_QUANTITY: "UPDATE_QUANTITY",
	UPDATE_PRICE: "UPDATE_PRICE",
	REMOVE_ITEM: "REMOVE_ITEM",
	CLEAR_CART: "CLEAR_CART",
	SET_ITEMS: "SET_ITEMS",
	SET_FEEDBACK: "SET_FEEDBACK",
	CLEAR_FEEDBACK: "CLEAR_FEEDBACK",
};

/**
 * 將商品移到指定位置（PREPEND 或 APPEND）
 * @param {Array} items - 購物車項目陣列
 * @param {*} itemId - 要移動的商品 ID
 * @param {Object} updatedItem - 更新後的商品
 * @param {string} position - UPDATE_POSITION.PREPEND 或 APPEND
 * @returns {Array} 重新排序後的陣列
 */
const moveItemToPosition = (items, itemId, updatedItem, position) => {
	const others = items.filter(item => !isEqual(item.id, itemId));
	return position === UPDATE_POSITION.APPEND ? [...others, updatedItem] : [updatedItem, ...others];
};

/**
 * 初始狀態
 */
const initialState = {
	cartItems: [],
	feedback: null,
};

/**
 * Cart Reducer - 統一管理購物車狀態
 * @param {Object} state - 當前狀態
 * @param {Object} action - Action 物件
 * @returns {Object} 新狀態
 */
const cartReducer = (state, action) => {
	switch (action.type) {
		case CART_ACTIONS.ADD_ITEM: {
			const { product, quantity } = action.payload;

			if (!product || !product.id) {
				return {
					...state,
					feedback: {
						type: FEEDBACK_TYPES.ERROR,
						message: "商品資料無效",
						item: null,
						timestamp: Date.now(),
					},
				};
			}

			const existingItem = state.cartItems.find(item => isEqual(item.id, product.id));

			if (existingItem) {
				// 如果商品已存在，增加數量；依 addExistingPosition 決定是否調整位置（預設 KEEP 不動）
				const { addExistingPosition = UPDATE_POSITION.KEEP } = action.payload;
				const newQuantity = existingItem.quantity + quantity;
				const updatedItem = {
					...existingItem,
					quantity: newQuantity,
				};

				let newCartItems = state.cartItems.map(item => (isEqual(item.id, product.id) ? updatedItem : item));
				if (addExistingPosition === UPDATE_POSITION.PREPEND || addExistingPosition === UPDATE_POSITION.APPEND) {
					newCartItems = moveItemToPosition(newCartItems, product.id, updatedItem, addExistingPosition);
				}

				return {
					...state,
					cartItems: newCartItems,
					feedback: {
						type: FEEDBACK_TYPES.ADD_SUCCESS,
						message: `${product.name} 數量已更新為 ${newQuantity}`,
						item: updatedItem,
						timestamp: Date.now(),
					},
				};
			} else {
				// 如果商品不存在，新增到購物車
				const { addPosition = ADD_POSITION.PREPEND } = action.payload;
				const newItem = {
					...product,
					quantity,
				};
				const newCartItems =
					addPosition === ADD_POSITION.APPEND
						? [...state.cartItems, newItem]
						: [newItem, ...state.cartItems];

				return {
					...state,
					cartItems: newCartItems,
					feedback: {
						type: FEEDBACK_TYPES.ADD_SUCCESS,
						message: `${product.name} 已加入購物車`,
						item: newItem,
						timestamp: Date.now(),
					},
				};
			}
		}

		case CART_ACTIONS.UPDATE_QUANTITY: {
			const { itemId, newQuantity, updatePosition = UPDATE_POSITION.KEEP, removeWhenZero = true } = action.payload;

			const itemToUpdate = state.cartItems.find(item => isEqual(item.id, itemId));
			if (!itemToUpdate) return state;

			if (newQuantity <= 0) {
				// 數量 ≤0：依 removeWhenZero 決定移除或保留（數量改為 0）
				if (removeWhenZero) {
					return {
						...state,
						cartItems: state.cartItems.filter(item => !isEqual(item.id, itemId)),
						feedback: {
							type: FEEDBACK_TYPES.REMOVE_SUCCESS,
							message: `${itemToUpdate.name} 已從購物車移除`,
							item: itemToUpdate,
							timestamp: Date.now(),
						},
					};
				}
				// 保留商品，數量設為 0
				const updatedItem = { ...itemToUpdate, quantity: 0 };
				let newCartItems = state.cartItems.map(item => (isEqual(item.id, itemId) ? updatedItem : item));
				if (updatePosition === UPDATE_POSITION.PREPEND || updatePosition === UPDATE_POSITION.APPEND) {
					newCartItems = moveItemToPosition(newCartItems, itemId, updatedItem, updatePosition);
				}
				return {
					...state,
					cartItems: newCartItems,
					feedback: {
						type: FEEDBACK_TYPES.UPDATE_SUCCESS,
						message: `${itemToUpdate.name} 數量已更新為 0`,
						item: updatedItem,
						timestamp: Date.now(),
					},
				};
			}

			const updatedItem = {
				...itemToUpdate,
				quantity: newQuantity,
			};

			let newCartItems = state.cartItems.map(item => (isEqual(item.id, itemId) ? updatedItem : item));
			if (updatePosition === UPDATE_POSITION.PREPEND || updatePosition === UPDATE_POSITION.APPEND) {
				newCartItems = moveItemToPosition(newCartItems, itemId, updatedItem, updatePosition);
			}

			return {
				...state,
				cartItems: newCartItems,
				feedback: {
					type: FEEDBACK_TYPES.UPDATE_SUCCESS,
					message: `${itemToUpdate.name} 數量已更新為 ${newQuantity}`,
					item: updatedItem,
					timestamp: Date.now(),
				},
			};
		}

		case CART_ACTIONS.UPDATE_PRICE: {
			const { itemId, newPrice, updatePosition = UPDATE_POSITION.KEEP } = action.payload;
			const itemToUpdate = state.cartItems.find(item => isEqual(item.id, itemId));
			if (!itemToUpdate) return state;

			const updatedItem = {
				...itemToUpdate,
				currentPrice: newPrice,
			};

			let newCartItems = state.cartItems.map(item => (isEqual(item.id, itemId) ? updatedItem : item));
			if (updatePosition === UPDATE_POSITION.PREPEND || updatePosition === UPDATE_POSITION.APPEND) {
				newCartItems = moveItemToPosition(newCartItems, itemId, updatedItem, updatePosition);
			}

			return {
				...state,
				cartItems: newCartItems,
				feedback: {
					type: FEEDBACK_TYPES.UPDATE_SUCCESS,
					message: `${itemToUpdate.name} 價格已更新為 $${newPrice}`,
					item: updatedItem,
					timestamp: Date.now(),
				},
			};
		}

		case CART_ACTIONS.REMOVE_ITEM: {
			const { itemId } = action.payload;
			const itemToRemove = state.cartItems.find(item => isEqual(item.id, itemId));
			if (!itemToRemove) return state;

			return {
				...state,
				cartItems: state.cartItems.filter(item => !isEqual(item.id, itemId)),
				feedback: {
					type: FEEDBACK_TYPES.REMOVE_SUCCESS,
					message: `${itemToRemove.name} 已從購物車移除`,
					item: itemToRemove,
					timestamp: Date.now(),
				},
			};
		}

		case CART_ACTIONS.CLEAR_CART: {
			return {
				...state,
				cartItems: [],
				feedback: {
					type: FEEDBACK_TYPES.CLEAR_SUCCESS,
					message: "購物車已清空",
					item: null,
					timestamp: Date.now(),
				},
			};
		}

		case CART_ACTIONS.SET_ITEMS: {
			return {
				...state,
				cartItems: action.payload ?? [],
				feedback: null,
			};
		}

		case CART_ACTIONS.SET_FEEDBACK: {
			return {
				...state,
				feedback: action.payload,
			};
		}

		case CART_ACTIONS.CLEAR_FEEDBACK: {
			return {
				...state,
				feedback: null,
			};
		}

		default:
			return state;
	}
};

/**
 * 購物車業務邏輯 Hook
 * @param {Object} options - 選項
 * @param {string} options.addPosition - 新商品插入位置：ADD_POSITION.PREPEND（前）或 ADD_POSITION.APPEND（後），預設 PREPEND
 * @param {string} options.addExistingPosition - 重複加入同一商品時的位置：UPDATE_POSITION.PREPEND（前）、APPEND（後）或 KEEP（不動），預設 KEEP
 * @param {string} options.updatePosition - 更新商品時的位置：UPDATE_POSITION.PREPEND（前）、APPEND（後）或 KEEP（不動），預設 KEEP
 * @param {boolean} options.removeWhenZero - 數量更新為 ≤0 時是否移除商品，預設 true；false 時保留商品且數量為 0
 * @returns {Object} 購物車相關的狀態和方法
 */
const useCart = (options) => {
	const {
		addPosition = ADD_POSITION.PREPEND,
		addExistingPosition = UPDATE_POSITION.KEEP,
		updatePosition = UPDATE_POSITION.KEEP,
		removeWhenZero = true,
	} = options ?? {};

	// 使用 useReducer 管理購物車狀態
	const [state, dispatch] = useReducer(cartReducer, initialState);

	// 解構狀態
	const { cartItems, feedback } = state;

	/**
	 * 添加商品到購物車
	 * @param {Object} product - 商品資訊
	 * @param {number} quantity - 數量，預設為1
	 */
	const addToCart = useCallback(
		(product, quantity = 1) => {
			dispatch({
				type: CART_ACTIONS.ADD_ITEM,
				payload: { product, quantity, addPosition, addExistingPosition },
			});
		},
		// eslint-disable-next-line
		[addPosition, addExistingPosition]
	);

	/**
	 * 清除回饋訊息
	 */
	const clearFeedback = useCallback(
		() => {
			dispatch({
				type: CART_ACTIONS.CLEAR_FEEDBACK,
			});
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 更新商品數量
	 * @param {number} itemId - 商品ID
	 * @param {number} newQuantity - 新數量；≤0 時依 removeWhenZero 決定移除或保留為 0
	 */
	const updateQuantity = useCallback(
		(itemId, newQuantity) => {
			dispatch({
				type: CART_ACTIONS.UPDATE_QUANTITY,
				payload: { itemId, newQuantity, updatePosition, removeWhenZero },
			});
		},
		[updatePosition, removeWhenZero]
	);

	/**
	 * 從購物車移除商品
	 * @param {number} itemId - 商品ID
	 */
	const removeCartItem = useCallback(
		itemId => {
			dispatch({
				type: CART_ACTIONS.REMOVE_ITEM,
				payload: { itemId },
			});
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 更新商品價格
	 * @param {number} itemId - 商品ID
	 * @param {number} newPrice - 新價格
	 */
	const updatePrice = useCallback(
		(itemId, newPrice) => {
			dispatch({
				type: CART_ACTIONS.UPDATE_PRICE,
				payload: { itemId, newPrice, updatePosition },
			});
		},
		// eslint-disable-next-line
		[updatePosition]
	);

	/**
	 * 清空購物車
	 */
	const clearCart = useCallback(
		() => {
			dispatch({
				type: CART_ACTIONS.CLEAR_CART,
			});
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 直接設定購物車項目（用於 initialProducts 等情境，不觸發 feedback）
	 * @param {Array} items - 商品陣列，每項需有 id、quantity
	 */
	const setCartItems = useCallback(
		(items) => {
			dispatch({
				type: CART_ACTIONS.SET_ITEMS,
				payload: Array.isArray(items) ? items : [],
			});
		},
		// eslint-disable-next-line
		[]
	);

	// 計算購物車統計資訊
	const cartSummary = useMemo(
		() => {
			const subtotal = cartItems.reduce((sum, item) => sum + (item.currentPrice ?? 0) * item.quantity, 0);
			const itemCount = cartItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);

			return {
				subtotal,
				itemCount,
				isEmpty: cartItems.length === 0,
			};
		},
		// eslint-disable-next-line
		[cartItems]
	);

	/**
	 * 驗證購物車狀態
	 * @returns {Object} 驗證結果
	 */
	const validateCart = useCallback(
		() => {
			const errors = [];

			if (cartItems.length === 0) {
				errors.push("購物車為空");
			}

			return {
				isValid: errors.length === 0,
				errors,
			};
		},
		// eslint-disable-next-line
		[cartItems.length]
	);

	return {
		// 狀態
		cartItems,
		cartSummary,

		// 回饋狀態 - 結構化物件
		feedback,

		// 購物車操作方法
		addToCart,
		updateQuantity,
		removeCartItem,
		updatePrice,
		clearCart,
		setCartItems,
		clearFeedback,

		// 業務邏輯方法
		validateCart,
	};
};

export default useCart;
